import type { ProvePrefillVerifyResponse } from '~/services/identity/authenticated/prove-prefill.service.api';

export type PrefilledKycSnapshot = {
  firstName?: string;
  lastName?: string;
  phone?: string;
  dob?: { day?: string; month?: string; year?: string };
  address?: string;
  city?: string;
  state?: string;
  zip?: string;
};

export function createPrefilledKycSnapshot(data: ProvePrefillVerifyResponse): PrefilledKycSnapshot {
  const addr = data?.addresses?.[0];

  return {
    firstName: data?.firstName ? String(data.firstName) : undefined,
    lastName: data?.lastName ? String(data.lastName) : undefined,
    phone: data?.phoneNumber ? String(data.phoneNumber) : undefined,
    dob: data?.dateOfBirth
      ? {
          day: data.dateOfBirth?.day !== undefined ? String(data.dateOfBirth.day) : undefined,
          month: data.dateOfBirth?.month !== undefined ? String(data.dateOfBirth.month) : undefined,
          year: data.dateOfBirth?.year !== undefined ? String(data.dateOfBirth.year) : undefined,
        }
      : undefined,
    address: addr?.address ? String(addr.address) : undefined,
    city: addr?.city ? String(addr.city) : undefined,
    state: addr?.region ? String(addr.region) : undefined,
    zip: addr?.postalCode ? String(addr.postalCode) : undefined,
  };
}

export function didPrefilledValuesChange(
  snapshot: PrefilledKycSnapshot,
  current: {
    firstName?: string;
    lastName?: string;
    phone?: string;
    dob: { day?: string | number; month?: string | number; year?: string | number };
    address?: string;
    city?: string;
    state?: string;
    zip?: string;
  }
): boolean {
  const normalize = (v?: string) => (v ?? '').trim();
  const normalizeDob = (v?: string | number) =>
    v === undefined || v === null ? '' : String(v).trim();

  return (
    (snapshot.firstName !== undefined &&
      normalize(snapshot.firstName) !== normalize(current.firstName)) ||
    (snapshot.lastName !== undefined &&
      normalize(snapshot.lastName) !== normalize(current.lastName)) ||
    (snapshot.phone !== undefined && normalize(snapshot.phone) !== normalize(current.phone)) ||
    (snapshot.dob?.day !== undefined &&
      normalizeDob(snapshot.dob?.day) !== normalizeDob(current.dob?.day)) ||
    (snapshot.dob?.month !== undefined &&
      normalizeDob(snapshot.dob?.month) !== normalizeDob(current.dob?.month)) ||
    (snapshot.dob?.year !== undefined &&
      normalizeDob(snapshot.dob?.year) !== normalizeDob(current.dob?.year)) ||
    (snapshot.address !== undefined &&
      normalize(snapshot.address) !== normalize(current.address)) ||
    (snapshot.city !== undefined && normalize(snapshot.city) !== normalize(current.city)) ||
    (snapshot.state !== undefined && normalize(snapshot.state) !== normalize(current.state)) ||
    (snapshot.zip !== undefined && normalize(snapshot.zip) !== normalize(current.zip))
  );
}

export function errorToString(e: unknown): string {
  if (typeof e === 'string') return e;
  if (e && typeof e === 'object') {
    const anyErr = e as any;
    if (anyErr?.errorMessage) {
      return Array.isArray(anyErr.errorMessage)
        ? anyErr.errorMessage.join(', ')
        : String(anyErr.errorMessage);
    }
    if (anyErr?.message) return String(anyErr.message);
  }
  return 'Unknown error';
}
